/**
 * "Edit this wording in the Email Library" — the link from a drafted email to the template it came
 * from.
 *
 * Two decisions worth keeping:
 *
 * 1. **A NEW TAB, always.** Every caller is a modal the recruiter is mid-edit in — the Archive
 *    modal's rejection body, the bulk-invite preview — and same-tab navigation would throw those
 *    edits away to answer a question as small as "what does this template actually say".
 * 2. **Rendered only when there IS a key.** Not every drafted email in the app comes from the
 *    Library: the Candidate Requests tab's "standard reply" is an in-code template
 *    (`proxy-server/lib/inboundReply.js`, which never reads `emailTemplates.js`), so a link there
 *    would point at a template that does not exist. Absent key, absent link — no dead ends.
 */
import { ExternalLink } from 'lucide-react';

export function EmailLibraryLink({
  templateKey,
  label = 'Edit this wording in the Email Library',
}: {
  /** The template's `key`, not its Firestore doc id — see EmailLibraryView's `?template=` handling. */
  templateKey?: string | null;
  label?: string;
}) {
  const key = `${templateKey || ''}`.trim();
  if (!key) return null;
  return (
    <a
      href={`/email-library?template=${encodeURIComponent(key)}`}
      target="_blank"
      rel="noreferrer"
      // The modals that host this all toggle on click somewhere up the tree.
      data-no-toggle
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
      title={`Opens the Email Library in a new tab, on the "${key}" template. Your edits here are not lost.`}
    >
      <ExternalLink className="h-3 w-3" /> {label}
    </a>
  );
}
